import React, {useRef, useMemo, useState, useEffect} from 'react';
import {Alert, Button, Checkbox, Textarea, Typography, useBreakpointCurrent} from 'bonprix-design-system';
import Rating from '@article-overview/components/alpi/Rating';
import {useTrinity} from '@article-overview/components/hooks/use-trinity.js';

// Type definitions
type Step = 'rating' | 'followup' | 'thankyou';
type ReasonKey = 'wrong_articles' | 'too_few_results' | 'not_appealing' | 'other';

interface ReasonOption {
    key: ReasonKey;
    label: string;
}

// Constants
const REASON_OPTIONS: ReasonOption[] = [
    {key: 'wrong_articles', label: 'andere Artikel angezeigt als gesucht'},
    {key: 'too_few_results', label: 'zu wenig Ergebnisse'},
    {key: 'not_appealing', label: 'Suchergebnis nicht ansprechend genug'},
    {key: 'other', label: 'anderer Grund'},
];

const HEADINGS: Record<Step, string> = {
    rating: 'WIE ZUFRIEDEN BIST DU MIT DEM SUCHERGEBNIS?',
    followup: 'AUS WELCHEM GRUND BIST DU UNZUFRIEDEN?',
    thankyou: 'VIELEN DANK',
};

const LABELS = {
    left: 'sehr unzufrieden',
    right: 'sehr zufrieden',
};

const VALIDATION_MESSAGES = {
    noReasonSelected: 'Bitte wähle mindestens einen Grund aus.',
    otherReasonEmpty: 'Bitte beschreibe deinen anderen Grund.',
};

interface SurveyProps {
    searchQuery?: string;
}

export const Survey = ({searchQuery}: SurveyProps) => {
    const currentBreakpoint = useBreakpointCurrent();
    const containerRef = useRef<HTMLDivElement>(null);

    const {sendSearchSurveyView, sendSearchSurveyClick} = useTrinity();
    const viewEventSent = useRef(false);


    // State
    const [step, setStep] = useState<Step>('rating');
    const [rating, setRating] = useState(0);
    const [selectedReasons, setSelectedReasons] = useState<ReasonKey[]>([]);
    const [otherReasonText, setOtherReasonText] = useState('');
    const [validationError, setValidationError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const heading = useMemo(() => HEADINGS[step], [step]);

    // VIEW_SEARCH_SURVEY tracking
    useEffect(() => {
        const element = containerRef.current;
        if (!element || viewEventSent.current || currentBreakpoint !== 's') return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !viewEventSent.current) {
                    sendSearchSurveyView(searchQuery || '');
                    viewEventSent.current = true;
                    observer.disconnect();
                }
            });
        }, {threshold: 0});

        observer.observe(element);
        return () => observer.disconnect();
    }, [currentBreakpoint, searchQuery]);

    // Only render on mobile
    if (currentBreakpoint !== 's') {
        return null;
    }

    // Validation helper
    const validateForm = (): boolean => {
        if (selectedReasons.length === 0) return false;
        if (selectedReasons.includes('other') && !otherReasonText.trim()) return false;
        return true;
    };

    const getValidationMessage = (): string => {
        if (selectedReasons.length === 0) return VALIDATION_MESSAGES.noReasonSelected;
        return VALIDATION_MESSAGES.otherReasonEmpty;
    };

    // Event handlers
    const handleRatingChange = (newRating: number): void => {
        if (newRating === rating) {
            setRating(0);
            return;
        }

        setRating(newRating);

        sendSearchSurveyClick({
            clickName: 'starRating',
            starRatingValue: newRating,
            object: 'icon',
            searchTerm: searchQuery || '',
        });
    };

    const handleRatingSubmit = (): void => {
        if (rating === 0) return;
        setStep(rating >= 4 ? 'thankyou' : 'followup');
    };

    const handleReasonToggle = (reason: ReasonKey, isChecked: boolean): void => {
        setSelectedReasons((prev) =>
            isChecked ? [...prev, reason] : prev.filter((r) => r !== reason)
        );

        if (!isChecked && reason === 'other') {
            setOtherReasonText('');
        }

        setValidationError(false);
    };

    const handleOtherTextChange = (text: string): void => {
        setOtherReasonText(text);
        setValidationError(false);
    };

    const handleSubmit = (): void => {
        if (!validateForm()) {
            setValidationError(true);
            return;
        }

        setIsSubmitting(true);
        setValidationError(false);

        const selectedLabels = selectedReasons.map(
            (key) => REASON_OPTIONS.find((opt) => opt.key === key)?.label
        ).filter(Boolean);

        sendSearchSurveyClick({
            clickName: 'submitSurvey',
            starRatingValue: rating,
            object: 'button',
            searchTerm: searchQuery || '',
            ...(selectedLabels.length > 0 ? {checkboxAnswers: JSON.stringify(selectedLabels)} : {}),
            ...(selectedReasons.includes('other') && otherReasonText ? {freeTextAnswer: otherReasonText} : {}),
        });

        setTimeout(() => {
            setIsSubmitting(false);
            setStep('thankyou');
            containerRef.current?.scrollIntoView({behavior: 'instant', block: 'center'});
            window.scrollBy(0, -80);
        }, 1000);
    };

    // Render helpers
    const renderFollowupView = () => (
        <div
            className="w-full flex flex-col gap-[1rem] bg-clr-white p-[1.5rem] rounded"
            role="group"
            aria-labelledby="followup-question"
        >
            {REASON_OPTIONS.map(({key, label}) => (
                <Checkbox
                    key={key}
                    label={label}
                    checked={selectedReasons.includes(key)}
                    onChange={(e) => handleReasonToggle(key, e)}
                    name={`reason-${key}`}
                />
            ))}

            {selectedReasons.includes('other') && (
                <div className="w-full">
                    <Textarea
                        label="Warum bist Du unzufrieden?"
                        value={otherReasonText}
                        onChange={handleOtherTextChange}
                        maxLength={500}
                        name="other-reason-text"
                        aria-label="Warum bist Du unzufrieden?"
                    />
                </div>
            )}

            {validationError && (
                <Alert isOpen status="error" showCloseIcon={false}>
                    {getValidationMessage()}
                </Alert>
            )}

            <Button onClick={handleSubmit} loading={isSubmitting} disabled={isSubmitting} size="large">
                ABSENDEN
            </Button>
        </div>
    );

    return (
        <div
            ref={containerRef}
            className={`col-span-full w-full flex flex-col items-center gap-[1.75rem] pt-12 px-2 ${
                step !== 'followup' ? 'pb-12' : 'pb-3'
            } bg-[#D0E3CD] border border-[#0066CC] rounded`}
        >
            <div className="px-2">
                <Typography component="h2" variant="bonprix-h2" align="center">
                    {heading}
                </Typography>
            </div>

            {step === 'thankyou' && (
                <Typography component="p" variant="subtitle1" align="center">
                    Deine Meinung hilft uns weiter
                </Typography>
            )}

            <Rating
                rating={rating}
                onChange={handleRatingChange}
                size="large"
                readonly={step !== 'rating'}
                showGuideLines={step === 'rating'}
                labels={step === 'rating' ? LABELS : undefined}
                aria-labelledby="survey-question"
                aria-label="Zufriedenheitsbewertung von 1 bis 5 Sternen"
            />

            {step === 'rating' && rating > 0 && (
                <div className="w-[16rem]">
                    <Button onClick={handleRatingSubmit} size="large" variant="primary">
                        ABSENDEN
                    </Button>
                </div>
            )}

            {step === 'followup' && renderFollowupView()}
        </div>
    );
};
